import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToSection } from "../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.15, ease: EASE },
  }),
};

const fadeVariant = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: d, ease: EASE } }),
};

export default function Hero({ ready }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const state = ready ? "show" : "hidden";

  return (
    <section id="start" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y }}>
        <motion.img
          src="/images/hero.jpg"
          alt="Billardtisch im warmen Licht mit Queue und Kugeln"
          className="h-full w-full object-cover object-[center_60%]"
          initial={{ scale: 1.18 }}
          animate={ready ? { scale: 1.06 } : {}}
          transition={{ duration: 2.8, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#120d0b] via-[#120d0b]/45 to-[#120d0b]/25" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 md:px-10">
        <motion.p
          data-testid="hero-eyebrow"
          className="mb-6 text-xs uppercase tracking-[0.3em] text-[#c9a24a]"
          variants={fadeVariant}
          custom={0.1}
          initial="hidden"
          animate={state}
        >
          Billard & Lounge — Bremen-Hemelingen
        </motion.p>

        <h1 className="font-display font-light leading-[0.95] text-[#f4ecdd]">
          <span className="block overflow-hidden">
            <motion.span
              data-testid="hero-headline-1"
              className="block text-[16vw] md:text-[9rem]"
              variants={lineVariant}
              custom={0}
              initial="hidden"
              animate={state}
            >
              VINTAGE
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              data-testid="hero-headline-2"
              className="block text-[16vw] italic text-[#e3c988] md:text-[9rem]"
              variants={lineVariant}
              custom={1}
              initial="hidden"
              animate={state}
            >
              BILLARD
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          className="mt-8 font-display text-xl italic text-[#e3c988] md:text-2xl"
          variants={fadeVariant}
          custom={0.85}
          initial="hidden"
          animate={state}
        >
          Deine Auszeit am Billardtisch
        </motion.p>

        <motion.p
          data-testid="hero-intro"
          className="mt-4 max-w-xl text-base leading-relaxed text-[#c9bda9] md:text-lg"
          variants={fadeVariant}
          custom={1.0}
          initial="hidden"
          animate={state}
        >
          Ob spannendes Duell, entspannter Feierabend oder geselliger Abend mit Freunden – bei
          Vintage Billard findest du den richtigen Ort, um abzuschalten, zu genießen und gemeinsam
          eine gute Zeit zu verbringen.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap gap-4"
          variants={fadeVariant}
          custom={1.15}
          initial="hidden"
          animate={state}
        >
          <button
            data-testid="hero-cta-angebot"
            onClick={() => scrollToSection("angebot")}
            className="bg-[#5c1a1f] px-8 py-4 text-sm uppercase tracking-[0.18em] text-[#f4ecdd] transition-colors duration-300 hover:bg-[#3c1013]"
          >
            Unser Angebot
          </button>
          <button
            data-testid="hero-cta-preise"
            onClick={() => scrollToSection("preise")}
            className="border border-[#c9a24a] px-8 py-4 text-sm uppercase tracking-[0.18em] text-[#c9a24a] transition-colors duration-300 hover:bg-[#c9a24a] hover:text-[#120d0b]"
          >
            Öffnungszeiten & Preise
          </button>
          <button
            data-testid="hero-cta-speisekarte"
            onClick={() => scrollToSection("speisekarte")}
            className="border border-[#c9a24a] px-8 py-4 text-sm uppercase tracking-[0.18em] text-[#c9a24a] transition-colors duration-300 hover:bg-[#c9a24a] hover:text-[#120d0b]"
          >
            Speisekarte
          </button>
        </motion.div>
      </div>

      <motion.div
        data-testid="hero-scroll-hint"
        className="absolute bottom-8 right-8 hidden flex-col items-center gap-3 md:flex"
        variants={fadeVariant}
        custom={1.5}
        initial="hidden"
        animate={state}
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#c9bda9]/70 [writing-mode:vertical-rl]">
          Scroll
        </span>
        <motion.span
          className="h-14 w-px bg-[#c9a24a]/60"
          style={{ originY: 0 }}
          animate={{ scaleY: [1, 0.35, 1] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}
